import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  Input,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useColorMode,
  useColorModeValue,
  useToast,
  VStack,
} from "@chakra-ui/react";
import Lottie from "react-lottie";
import lightOverlayAnimationData from "../assets/loginFormBgLight.json";
import darkOverlayAnimationData from "../assets/loginFormBgDark.json";
import lightAnimationData from "../assets/BgLight.json";
import darkAnimationData from "../assets/BgDark.json";
import { useNavigate } from "react-router-dom";
import { useLoading } from "../context/LoadingContext";
import { ToggleColorModeButton } from "../theme/ToggleColorModeButton";
import { LoadingAnimation } from "./LoadingAnimation";
import API, { dashboardURL } from "../context/API";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { FaUsers, FaUserEdit, FaUser } from "react-icons/fa";
import axios from "axios";

const LoginPage = ({ isLoggedIn, setIsLoggedIn, setRole, setLoginData }) => {
  const toast = useToast();
  const [emailOrUsername, setEmailUsername] = useState("");
  const [password, setPassword] = useState("");
  const { setLoading } = useLoading();
  const { colorMode } = useColorMode();
  const navigate = useNavigate();
  const { isLoading } = useLoading();
  const [isOpen, setIsOpen] = useState(false);
  const [menuButtonWidth, setMenuButtonWidth] = useState("auto");
  const menuButtonRef = useRef(); // Ref for MenuButton

  const textColor = useColorModeValue("gray.800", "gray.100");
  const hostname = window.location.hostname;

  // Add environment-based URL handling
  const getDashboardUrl = (token, role) => {
    const isProduction = hostname.includes('kiwikrave.com');
    return isProduction
      ? `${dashboardURL}/dashboard?access_token=${token}&role=${role}`
      : `/dashboard?access_token=${token}&role=${role}`;
  };

  // const handleToggle = () => setIsOpen(!isOpen);

  // This useEffect will update the width of the menu list when the component is rendered
  useEffect(() => {
    if (menuButtonRef.current) {
      setMenuButtonWidth(`${menuButtonRef.current.offsetWidth}px`);
    }
  }, []);

  const submit = async (e, role) => {
    e.preventDefault();

    const user = {
      emailOrUsername: emailOrUsername,
      password: password,
      source: "web",
    };

    try {
      const { data } = await API.post(`${role}/login/`, user, {
        headers: { "Content-Type": "application/json" },
        withCredentials: false,
      });
      if (data.status === 200) {
        if(role !== "merchant"){
          showToast("Login Successful.", "You are now Signed in.", "success");
          setLoading(true);
          setTimeout(() => {
            setIsLoggedIn(true);
            setLoginData(data.data);
            setRole(role);
            axios.defaults.headers.common["Authorization"] = `Bearer ${data["access"]}`;
            window.location.href = getDashboardUrl(data.data.jwt, role);
            setLoading(false);
          }, 1000);
        } else {
          setLoading(true);
          setTimeout(() => {
            setLoginData(data.data);
            setRole(role);
            localStorage.setItem("role", role);
            axios.defaults.headers.common["Authorization"] = `Bearer ${data["access"]}`;

            if(data.data.otp && data.data.phone){
              navigate("/otpverify");
            } else {
              window.location.href = getDashboardUrl(data.data.jwt, role);
            }

            setLoading(false);
          }, 1000);
        }
      } else {
        throw new Error(`Request failed with status ${data.status}`);
      }
    } catch (error) {
      console.error("Login error:", error);
      showToast(
        "Unable To Login.",
        error.response?.data?.detail || error.response?.data?.message || error.message,
        "error"
      );
    }
  };

  function showToast(title, description, status) {
    toast({
      title,
      description,
      status,
      duration: 1000,
      isClosable: true,
    });
  }

  const baseAnimationData =
    colorMode === "light" ? lightAnimationData : darkAnimationData;
  const overlayAnimationData =
    colorMode === "light"
      ? lightOverlayAnimationData
      : darkOverlayAnimationData;

  const baseOptions = {
    loop: false,
    autoplay: true,
    animationData: baseAnimationData,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  };

  const overlayOptions = {
    loop: false,
    autoplay: true,
    animationData: overlayAnimationData,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid meet",
    },
  };

  return (
    <Box position="relative" height="100vh" width="100vw">
      {!isLoading ? (
        <>
          <Box
            position="absolute"
            top="0"
            left="0"
            right="0"
            bottom="0"
            overflow="hidden"
            pointerEvents={"none"}
          >
            <Box position="absolute" width="full" height="full">
              <Lottie options={baseOptions} />
            </Box>
            <Box position="absolute" width="full" height="full">
              <Lottie options={overlayOptions} />
            </Box>
          </Box>
          <ToggleColorModeButton
            width="24px"
            height="24px"
            iconButtonProps={{
              position: "absolute",
              top: "5",
              right: "5",
            }}
          />

          <VStack
            align="stretch"
            spacing={4}
            p={8}
            rounded="lg"
            maxWidth="400px"
            margin="auto"
            position="relative"
            top="50%"
            transform="translateY(-50%)"
            color={"black"}
          >
            <Text fontSize="2xl" fontWeight="bold" alignSelf="center">
              Login
            </Text>
            <FormControl id="email" isRequired>
              <FormLabel>Email</FormLabel>
              <Input
                type="emailOrUsername"
                placeholder="Enter your email/username here"
                value={emailOrUsername}
                variant={"filled"}
                borderRadius={"full"}
                onChange={(e) => setEmailUsername(e.target.value)}
              />
            </FormControl>
            <FormControl id="password" isRequired>
              <FormLabel 
                fontSize={{ base: "sm", md: "md" }}
                color={textColor}
              >
                Password
              </FormLabel>
              <Input
                type="password"
                placeholder="Password"
                value={password}
                variant={"filled"}
                borderRadius={"full"}
                size={{ base: "md", md: "lg" }}
                fontSize={{ base: "sm", md: "md" }}
                onChange={(e) => setPassword(e.target.value)}
              />
            </FormControl>
            
            <Text textAlign="right" fontSize={{ base: "xs", md: "sm" }} mb={2}>
              <Link 
                color="blue.500" 
                onClick={() => navigate("/forgot-password")} 
                _hover={{ textDecoration: "underline" }}
                cursor="pointer"
                fontWeight="medium"
              >
                Forgot Password?
              </Link>
            </Text>
            <FormControl>
              <VStack spacing={4} align="stretch">
                {
                  hostname.includes('admin') ?(
                  <Button
                    colorScheme="green"
                    onClick={(e) => submit(e, "admin")}
                    w="full"
                    p={{ base: 3, md: 4 }}
                    borderRadius="full"
                    fontSize={{ base: "sm", md: "md" }}
                    fontWeight="bold"
                    boxShadow="md"
                    _hover={{ boxShadow: "lg" }}
                    size={{ base: "md", md: "lg" }}
                  >
                    Login as Admin
                </Button>):(
                  (hostname.includes('merchant.kiwikrave.com') || hostname.includes('kiwikrave.com') || hostname.includes('localhost')) &&
                  <Button
                    colorScheme="green"
                    onClick={(e) => submit(e, "merchant")}
                    w="full"
                    p={{ base: 3, md: 4 }}
                    borderRadius="full"
                    fontSize={{ base: "sm", md: "md" }}
                    fontWeight="bold"
                    boxShadow="md"
                    _hover={{ boxShadow: "lg" }}
                    size={{ base: "md", md: "lg" }}
                  >
                    Login as Merchant
                  </Button>
                )
                }
              </VStack>
            </FormControl>
          </VStack>
        </>
      ) : (
        <>
          <LoadingAnimation size="large" />
        </>
      )}
    </Box>
  );
};

export default LoginPage;

